import React from 'react';
import {
  Flex,
  View,
  TooltipTrigger,
  Tooltip,
  ActionButton
} from '@adobe/react-spectrum';
import { EditTitle } from './EditTitle';
import { RiskLevel } from './RiskLevel';
import { DateComponent } from './DateComponent';
import { EditReportingSummary } from './EditReportingSummary';
import { ProductIcons } from './ProductIcons';
import { EventDetails } from './EventDetails';
import styles from './TimelineCard.module.scss';
import { useEventKeyDatePermissions } from 'app/utils/rbacHandler';
import { useLocalTimestamp } from './useLocalTimestamp';
import moment from 'moment';
import {
  MOMENT_TYPES,
  isEolMoment,
  isPriceChangeStyleOfferMoment
} from 'utils/constants';
import {
  resolveCustomerNotificationRawValue,
  resolveEffectiveRawValue
} from 'utils/timelineDisplayDates';
import { resolvePhaseDisplayName } from 'utils/rtmPhaseDisplayTitle';
import { isFiscalQuarterInPast } from '../../../utils/time';

interface DateTooltipProps {
  isDateClicked: boolean;
  setIsDateClicked: (isDateClicked: boolean) => void;
  dateTooltipRef: React.RefObject<HTMLDivElement>;
  categories?: string[];
  products?: string[];
  title: string;
  /** Raw moment name for API (no RTM phase prefix). Same as title when not phased. */
  storageTitle?: string;
  /** When set, EditTitle shows "{label}: {storage}" but PATCHes only storage text. */
  phaseTitleLabel?: string;
  isClosed: boolean;
  riskLevel?: string;
  issueId: string;
  getStatusIcon: () => React.ReactNode;
  handleTooltipMouseEnter: (key: string) => void;
  handleTooltipMouseLeave: () => void;
  lastUpdated?: string;
  reportingSummary?: string;
  handleImageError: (event: any) => void;
  wholeCard: any;
  tooltipPlacement?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  isOverdue?: boolean; // ✅ NEW: Flag to indicate if dates are overdue
  /**
   * Forces the tooltip to render fully opaque/visible regardless of the
   * `:hover`/`.dateTooltipVisible` CSS rules in TimelineCard.module.scss,
   * which only apply while this element is a DOM descendant of its
   * `.dateContainer` trigger. Needed by callers (e.g. Review & Approval's
   * ReleaseMomentDateCell) that portal this component elsewhere in the DOM
   * to escape an ancestor's clipping/overflow, where those descendant
   * selectors can no longer match. Unused by TimelineCard.tsx itself.
   */
  alwaysVisible?: boolean;
  refreshSource?: 'reviewApproval';
}

export const DateTooltip: React.FC<DateTooltipProps> = props => {
  const {
    isDateClicked,
    setIsDateClicked,
    dateTooltipRef,
    categories,
    products,
    title,
    storageTitle,
    phaseTitleLabel,
    isClosed,
    riskLevel,
    issueId,
    getStatusIcon,
    handleTooltipMouseEnter,
    handleTooltipMouseLeave,
    lastUpdated,
    reportingSummary,
    handleImageError,
    wholeCard,
    tooltipPlacement = { vertical: 'top', horizontal: 'left' },
    isOverdue = false, // ✅ NEW: Default to false if not provided
    alwaysVisible = false
  } = props;
  const lastUpdatedDate = useLocalTimestamp(lastUpdated || '');
  const { isAdmin } = useEventKeyDatePermissions();
  // const isAdmin = true;
  const canEdit: boolean = wholeCard?.canEdit || false; // This needs to be taken from the MOments API (later)
  const canEditTooltip =
    (canEdit && isDateClicked) || (isAdmin && isDateClicked);
  const canSeeTooltipHover = canEdit || isAdmin;

  const phasePillLabel =
    phaseTitleLabel?.trim() ||
    resolvePhaseDisplayName({
      releasePhase: wholeCard?.releasePhase,
      releasePhaseId: wholeCard?.releasePhaseId,
      releasePhases: Array.isArray(wholeCard?.releasePhases)
        ? wholeCard.releasePhases
        : null
    }) ||
    undefined;

  // ✅ Centralized dropdown state management - only one dropdown can be open at a time
  const [openDropdown, setOpenDropdown] = React.useState<
    'release' | 'effective' | 'genai' | 'risk' | null
  >(null);

  const handleDropdownToggle = (
    dropdownName: 'release' | 'effective' | 'genai' | 'risk'
  ) => {
    setOpenDropdown(prev => (prev === dropdownName ? null : dropdownName));
  };

  const closeAllDropdowns = () => {
    setOpenDropdown(null);
  };

  // ✅ NEW: Compute separate overdue status for each date type
  // Helper function to check if a specific date is overdue
  const isDateOverdue = (dateValue: string | undefined): boolean => {
    if (!dateValue || isClosed) return false;

    // Handle quarter format (e.g., "Q3 2024")
    const quarterMatch = dateValue.match(/^Q(\d)\s+(\d{4})$/);
    if (quarterMatch) {
      return isFiscalQuarterInPast(dateValue);
    }

    // Handle month format (e.g., "September 2024" or "Sep 2024")
    const monthMatch = dateValue.match(/^(\w+)\s+(\d{4})$/);
    if (monthMatch) {
      const monthName = monthMatch[1];
      const year = parseInt(monthMatch[2]);
      // Parse month name to month number
      const monthDate = moment(`${monthName} ${year}`, 'MMMM YYYY');
      if (!monthDate.isValid()) {
        // Try short format
        const shortMonthDate = moment(`${monthName} ${year}`, 'MMM YYYY');
        if (shortMonthDate.isValid()) {
          return shortMonthDate.endOf('month').isBefore(moment(), 'day');
        }
      } else {
        return monthDate.endOf('month').isBefore(moment(), 'day');
      }
    }

    // Handle date format (e.g., "2024-09-01")
    if (dateValue.includes('-')) {
      return moment(dateValue).isBefore(moment(), 'day');
    }

    return false;
  };

  // Determine which dates to check based on project type (+ PCO flag for legacy-type new moments)
  const projectType = wholeCard?.projectType;
  const pcoFlag = wholeCard?.pco === true;
  const dates = wholeCard?.dates || {};

  // ✅ FIX: For Marketing Moments/Offers, the "Release Date" section shows targetStart
  // For other types, it shows releaseDate
  let releaseDateValue: string | undefined;
  if (isPriceChangeStyleOfferMoment(projectType, pcoFlag)) {
    // For Price Changes and Offers: "Release Date" section shows customerNotificationDate,
    // falling back to targetStart when notification date is not yet populated.
    releaseDateValue =
      dates.customerNotificationDate ||
      (dates as any).customerNotificationMonth ||
      dates.customerNotificationDateMonth ||
      (dates as any).customerNotificationQuarter ||
      dates.customerNotificationDateQuarter ||
      dates.targetStart ||
      dates.targetMonth ||
      dates.targetQuarter;
  } else if (isEolMoment(projectType)) {
    releaseDateValue = resolveCustomerNotificationRawValue(dates);
  } else if (
    projectType === MOMENT_TYPES.PROMOS_OFFERS ||
    projectType === MOMENT_TYPES.MARKETING_MOMENT
  ) {
    // For Marketing Moments/Promos: "Release Date" section displays targetStart/targetMonth/targetQuarter.
    // Phased "Offer, Price Change, or Program" cards use customerNotificationDate instead of targetStart.
    // API returns 'customerNotificationMonth'/'customerNotificationQuarter' (no "Date" infix)
    releaseDateValue =
      dates.targetStart ||
      dates.targetMonth ||
      dates.targetQuarter ||
      dates.customerNotificationDate ||
      (dates as any).customerNotificationMonth ||
      dates.customerNotificationDateMonth ||
      (dates as any).customerNotificationQuarter ||
      dates.customerNotificationDateQuarter;
  } else {
    // For other types: displays releaseDate/releaseMonth/releaseQuarter
    releaseDateValue =
      dates.releaseDate || dates.releaseMonth || dates.releaseQuarter;
  }
  const isReleaseOverdue = isDateOverdue(releaseDateValue);

  // For Effective Date: depends on project type
  let effectiveDateValue: string | undefined;
  if (isEolMoment(projectType)) {
    effectiveDateValue = resolveEffectiveRawValue(dates);
  } else if (
    isPriceChangeStyleOfferMoment(projectType, pcoFlag) ||
    projectType === MOMENT_TYPES.PROMOS_OFFERS ||
    projectType === MOMENT_TYPES.MARKETING_MOMENT
  ) {
    // For Price Changes and Offers / Marketing Moments / Promos: effective date uses effectiveDate fields
    effectiveDateValue =
      dates.effectiveDate || dates.effectiveMonth || dates.effectiveQuarter;
  } else {
    // For other types: uses targetStart/targetMonth/targetQuarter
    effectiveDateValue =
      dates.targetStart || dates.targetMonth || dates.targetQuarter;
  }
  const isEffectiveOverdue = isDateOverdue(effectiveDateValue);

  const tooltipClassName = `${styles.dateTooltip} ${
    isDateClicked ? styles.dateTooltipVisible : ''
  } ${!isDateClicked ? styles.dateTooltipReadOnly : ''} ${
    tooltipPlacement.vertical === 'bottom'
      ? styles.dateTooltipBottom
      : styles.dateTooltipTop
  } ${
    styles[
      `dateTooltip${
        tooltipPlacement.horizontal.charAt(0).toUpperCase() +
        tooltipPlacement.horizontal.slice(1)
      }`
    ]
  }`;

  return (
    <div
      ref={dateTooltipRef}
      className={tooltipClassName}
      style={alwaysVisible ? { opacity: 1, visibility: 'visible' } : undefined}
      onClick={e => {
        e.stopPropagation();
      }}
      data-testid={isDateClicked && 'render-date-popup'}
    >
      <div className={styles.dateTooltipContent}>
        <div className={styles.tooltipSection}>
          <Flex
            UNSAFE_className={styles.tooltipRow}
            gap="size-100"
            wrap="wrap"
            alignItems="center"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                gap: '8px'
              }}
            >
              <ProductIcons
                categories={[]}
                products={products}
                onError={handleImageError}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                minWidth: '200px'
              }}
            >
              <EditTitle
                title={title}
                storageTitle={storageTitle}
                phaseTitleLabel={phaseTitleLabel}
                releasePhase={wholeCard?.releasePhase}
                className={styles.tooltipValue}
                issueId={issueId}
                momentType={wholeCard?.projectType}
                isDateClicked={canEditTooltip}
              />
              <RiskLevel
                riskLevel={riskLevel}
                issueId={issueId}
                isDateClicked={canEditTooltip}
                isDropdownOpen={openDropdown === 'risk'}
                onDropdownToggle={() => handleDropdownToggle('risk')}
              />
            </div>
          </Flex>

          <EventDetails
            momentType={wholeCard?.projectType}
            surfaces={wholeCard?.surfaces}
            genAI={wholeCard?.genai}
            issueId={issueId}
            isDateClicked={canEditTooltip}
            wholeCard={wholeCard}
            phaseLabel={phasePillLabel}
            isGenAIDropdownOpen={openDropdown === 'genai'}
            onGenAIDropdownToggle={() => handleDropdownToggle('genai')}
          />

          {/* Release Date and Effective Date */}
          <Flex
            UNSAFE_className={styles.tooltipRow}
            gap="size-75"
            direction="column"
            alignItems="start"
          >
            <DateComponent
              rawDates={dates}
              isDateClicked={canEditTooltip}
              dateType="release"
              projectType={wholeCard?.projectType}
              pco={pcoFlag}
              isOverdue={isReleaseOverdue} // ✅ Only show red if THIS date is overdue
              isCalendarOpen={openDropdown === 'release'}
              onCalendarToggle={() => handleDropdownToggle('release')}
            />
            <DateComponent
              rawDates={dates}
              isDateClicked={canEditTooltip}
              dateType="effective"
              projectType={wholeCard?.projectType}
              pco={pcoFlag}
              isOverdue={isEffectiveOverdue} // ✅ Only show red if THIS date is overdue
              isCalendarOpen={openDropdown === 'effective'}
              onCalendarToggle={() => handleDropdownToggle('effective')}
            />
          </Flex>
          <EditReportingSummary
            reportingSummary={reportingSummary || ''}
            lastUpdated={lastUpdatedDate}
            isDateClicked={canEditTooltip}
            canSeeTooltipHover={canSeeTooltipHover}
            wholeCard={wholeCard}
            setIsDateClicked={setIsDateClicked}
            refreshSource={props.refreshSource}
          />
        </div>
      </div>
    </div>
  );
};
